// Danger zone: irreversible self-service account + data deletion.
//
// Deletes every record tied to the signed-in user (market targets, policies, alerts,
// audit history, notification settings, watchlist, and the linked Plaid item), revokes
// the Plaid access token, and removes the Cognito sign-in identity. Because it cannot be
// undone, the destructive button stays disabled until the user types DELETE to confirm.
// On success the user is signed out.

import { useState } from "react";

import { useApi } from "../api/useApi";
import { useAuth } from "../auth/useAuth";
import { useStepUp } from "../auth/StepUp";
import { Button } from "@/components/ui/button";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CONFIRM_WORD = "DELETE";

export function DeleteAccount() {
  const api = useApi();
  const { signOut } = useAuth();
  const { runWithStepUp, dialog } = useStepUp();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const canDelete = confirm.trim().toUpperCase() === CONFIRM_WORD && !busy;

  const remove = async () => {
    if (!canDelete) return;
    setBusy(true);
    setStatus("");
    try {
      // Deleting the account is sensitive: the backend requires a fresh auth, so wrap the
      // call so a step-up challenge prompts re-auth and retries.
      await runWithStepUp(() => api.deleteAccount());
      // Data and identity are gone; drop the local session so the user lands on sign-in.
      signOut();
    } catch (err) {
      setBusy(false);
      setStatus(err instanceof Error ? err.message : "Deletion failed");
    }
  };

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-lg text-destructive">Delete account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle>This cannot be undone</AlertTitle>
          <AlertDescription>
            <p>
              Deleting your account permanently removes all of your data. That includes
              your market targets and their alert policies, alert and audit history,
              notification settings, your watchlist, and any linked brokerage. If a
              brokerage is linked through Plaid, its access is revoked and the stored
              token is deleted. Your sign-in is removed, so you will not be able to log
              back in.
            </p>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="confirm-delete">
            Type {CONFIRM_WORD} to confirm
          </Label>
          <Input
            id="confirm-delete"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={CONFIRM_WORD}
            autoComplete="off"
            aria-label="confirm-delete"
          />
        </div>

        <Button
          type="button"
          variant="destructive"
          disabled={!canDelete}
          onClick={() => void remove()}
          data-testid="delete-account"
        >
          {busy ? "Deleting…" : "Delete my account and data"}
        </Button>

        {status && (
          <p className="status text-sm text-destructive" role="alert">
            {status}
          </p>
        )}
      </CardContent>
      {dialog}
    </Card>
  );
}
